import apiClient, { handleApiError } from '../lib/api-client';
import { API_ENDPOINTS } from '../config/api';
import { User } from '../store/auth-store';

export interface RegisterDto {
  email: string;
  password: string;
  phone?: string;
}

export interface LoginDto {
  email: string;
  password: string;
  deviceId?: string;
  deviceInfo?: string;
}

export interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  user: User;
}

export const authService = {
  async register(data: RegisterDto): Promise<AuthResponse> {
    try {
      const response = await apiClient.post<{ data: AuthResponse }>(
        API_ENDPOINTS.AUTH.REGISTER,
        data
      );
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  async login(data: LoginDto): Promise<AuthResponse> {
    try {
      const response = await apiClient.post<{ data: AuthResponse }>(
        API_ENDPOINTS.AUTH.LOGIN,
        data
      );
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  async logout(refreshToken?: string | null): Promise<void> {
    try {
      await apiClient.post(
        API_ENDPOINTS.AUTH.LOGOUT,
        refreshToken ? { refreshToken } : undefined
      );
    } catch (error) {
      // Log error but don't throw - user should still be logged out locally
      console.error('Logout error:', error);
    }
  },

  async refreshToken(refreshToken: string): Promise<{ accessToken: string }> {
    try {
      const response = await apiClient.post<{ data: { accessToken: string } }>(
        API_ENDPOINTS.AUTH.REFRESH,
        { refreshToken }
      );
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  async requestPasswordReset(email: string): Promise<void> {
    try {
      await apiClient.post(API_ENDPOINTS.AUTH.PASSWORD_RESET_REQUEST, { email });
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  async resetPassword(token: string, password: string): Promise<void> {
    try {
      await apiClient.post(API_ENDPOINTS.AUTH.PASSWORD_RESET, { token, password });
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  async changePassword(
    currentPassword: string,
    newPassword: string,
  ): Promise<{ message: string }> {
    try {
      const response = await apiClient.post<{ data?: { message: string }; message?: string }>(
        API_ENDPOINTS.AUTH.CHANGE_PASSWORD,
        { currentPassword, newPassword },
      );
      return (response.data.data || response.data) as { message: string };
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },
};

